import React from "react";
import styled from "@emotion/styled";
import { ResponsiveLine } from "@nivo/line";
import moment from "moment";
import { ProjectedOrganizationRevenue } from "../../../models/revenue/BillingConfig";
import { nivoChartTheme } from "../../../constants/chartTheme";
import {
    color_text_default,
    color_text_light,
    color_colors_ocean,
    color_defaults_white,
    color_shades_light,
    color_shades_lightest,
} from "../../../constants/colors";
import { chartSequentialBlues } from "../../../constants/chartTheme";

interface ProjectionsChartProps {
    projectedRevenueByOrg: ProjectedOrganizationRevenue[];
    allMonths: string[];
}

const ProjectionsChart: React.FC<ProjectionsChartProps> = ({
    projectedRevenueByOrg,
    allMonths,
}) => {
    if (allMonths.length === 0 || projectedRevenueByOrg.length === 0) {
        return null;
    }

    const hasData = projectedRevenueByOrg.some(
        (org) => org.monthlyProjections.length > 0
    );
    if (!hasData) return null;

    const colors = chartSequentialBlues || [
        "#1E7EB9",
        "#2196F3",
        "#42A5F5",
        "#64B5F6",
        "#90CAF9",
        "#BBDEFB",
    ];

    // --- Total revenue line data ---
    const totalByMonth: Record<string, number> = {};
    for (const org of projectedRevenueByOrg) {
        for (const mp of org.monthlyProjections) {
            const key = moment(mp.month, "YYYY-MM").format("MMM YY");
            totalByMonth[key] = (totalByMonth[key] || 0) + mp.projectedRevenue;
        }
    }
    const totalLineData = [
        {
            id: "Total Projected Revenue",
            color: "#2D2D39",
            data: allMonths.map((month) => ({
                x: moment(month, "YYYY-MM").format("MMM YY"),
                y: Math.round((totalByMonth[moment(month, "YYYY-MM").format("MMM YY")] || 0) * 100) / 100,
            })),
        },
    ];

    // --- Per-client line data ---
    const clientLineData = projectedRevenueByOrg
        .filter((org) => org.monthlyProjections.length > 0)
        .map((org, index) => ({
            id: org.organizationName,
            color: colors[index % colors.length],
            data: org.monthlyProjections.map((mp) => ({
                x: moment(mp.month, "YYYY-MM").format("MMM YY"),
                y: Math.round(mp.projectedRevenue * 100) / 100,
            })),
        }));

    const chartTheme = {
        ...nivoChartTheme,
        background: "#ffffff",
    };

    const renderChart = (data: typeof totalLineData, title: string) => (
        <ChartPanel>
            <ChartTitle>{title}</ChartTitle>
            <ChartWrapper>
                <ResponsiveLine
                    data={data}
                    enableArea={true}
                    areaOpacity={0.06}
                    animate={false}
                    curve="monotoneX"
                    enableGridX={false}
                    colors={(series: any) => series.color}
                    lineWidth={2.5}
                    pointSize={6}
                    pointBorderColor={color_defaults_white}
                    pointBorderWidth={1.5}
                    pointColor={(point: any) => point.serieColor}
                    useMesh={true}
                    enableSlices="x"
                    sliceTooltip={({ slice }: any) => (
                        <TooltipContainer>
                            <TooltipTitle>
                                {slice.points[0]?.data.xFormatted}
                            </TooltipTitle>
                            {slice.points.map((point: any) => (
                                <TooltipRow key={point.id}>
                                    <TooltipColorDot
                                        style={{ background: point.serieColor }}
                                    />
                                    <TooltipLabel>{point.serieId}</TooltipLabel>
                                    <TooltipValue>
                                        {formatCurrency(point.data.yFormatted as number)}
                                    </TooltipValue>
                                </TooltipRow>
                            ))}
                        </TooltipContainer>
                    )}
                    margin={{ top: 20, right: 20, bottom: 50, left: 70 }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                        tickSize: 0,
                        tickPadding: 8,
                        tickRotation: -45,
                    }}
                    axisLeft={{
                        tickSize: 0,
                        tickPadding: 8,
                        tickRotation: 0,
                        format: (value: number) => formatCompactCurrency(value),
                    }}
                    theme={chartTheme}
                    legends={[]}
                />
            </ChartWrapper>
            <LegendContainer>
                {data.map((series) => (
                    <LegendItem key={series.id}>
                        <LegendDot style={{ background: series.color }} />
                        <LegendLabel>{series.id}</LegendLabel>
                    </LegendItem>
                ))}
            </LegendContainer>
        </ChartPanel>
    );

    return (
        <ChartsRow>
            {renderChart(totalLineData, "Total Projected Revenue")}
            {renderChart(clientLineData, "Projected Revenue by Client")}
        </ChartsRow>
    );
};

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

const formatCompactCurrency = (value: number): string => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
};

const ChartsRow = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
`;

const ChartPanel = styled.div`
    flex: 1;
    min-width: 0;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    padding: 20px;
`;

const ChartTitle = styled.h3`
    margin: 0 0 16px 0;
    font-size: 14px;
    color: ${color_text_default};
`;

const ChartWrapper = styled.div`
    height: 350px;
`;

const LegendContainer = styled.div`
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 12px;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const LegendDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 2px;
`;

const LegendLabel = styled.span`
    font-size: 11px;
    color: ${color_text_light};
`;

const TooltipContainer = styled.div`
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    border: 1px solid ${color_shades_light};
    min-width: 160px;
`;

const TooltipTitle = styled.div`
    font-weight: 600;
    margin-bottom: 8px;
    color: ${color_text_default};
    font-size: 13px;
`;

const TooltipRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const TooltipColorDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
`;

const TooltipLabel = styled.span`
    font-size: 12px;
    color: ${color_text_light};
    flex: 1;
`;

const TooltipValue = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${color_text_default};
`;

export default React.memo(ProjectionsChart);
